import { useCallback, useMemo, memo, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { colors } from "@/utils/const";
import CustomText from "../text";
import OutImgSection from "../out-img-section";
import SmallTitle from "../mini-components/SmallTitle";
import Image from "next/image";
import en from "../../../locales/common/en";
import ar from "../../../locales/common/ar";
import SectionTopLine from "../section-top-line";
import { fontStyle, keyframes, lineHeight, textTransform } from "@mui/system";
import { useI18n } from "@/i18n/I18nProvider";

const floatFade = keyframes`
  0%, 100% { transform: translateY(0);    opacity: .85; }
  50%      { transform: translateY(-6px); opacity: 1;   }
`;

/**
 * Extracted + memoized: prevents re-definition and unnecessary re-renders.
 * Keeps hover state inside, so ProductMain doesn't rerender on hover.
 */
const ButtonsBox = memo(function ButtonsBox({
  locale,
  isArabic,
  t,
  isMobile,

  noCalc,
  onApplyClick,
  onCalcClick,
  applyText,
}) {
  const [hoverCalc, setHoverCalc] = useState(false);

  const handleCalcClick = useCallback(() => {
    setHoverCalc(false);
    onCalcClick?.();
  }, [onCalcClick]);

  const handleMouseEnter = useCallback(() => {
    if (!isMobile) setHoverCalc(true);
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (!isMobile) setHoverCalc(false);
  }, [isMobile]);

  return (
    <Box
      sx={{
        position: "fixed",
        zIndex: 999,
        bottom: { xs: "0px", sm: "auto" },
        top: { xs: "auto", sm: "40%" },
        transform: { xs: "none", sm: "translateY(-50%)" },
        left: { xs: 0, sm: locale === "ar" ? 0 : "auto" },
        right: { xs: 0, sm: locale === "en" ? 0 : "auto" },
        width: { xs: "100vw", sm: "auto" },
        margin: "auto",
        pb: { xs: "env(safe-area-inset-bottom)" },
        display: "flex",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "row", sm: "column" },
          alignItems: { xs: "stretch", sm: "end" },
          gap: { xs: 0, sm: 2 },
          width: { xs: "100%", sm: "auto" },
          backgroundColor: { xs: colors.orange, sm: "transparent" },
          overflow: { xs: "hidden", sm: "visible" },
        }}
      >
        {/* Apply Now */}
        <Typography
          onClick={onApplyClick}
          sx={{
            bgcolor: { xs: "transparent", sm: colors.orange },
            borderRadius: {
              xs: 0,
              sm: isArabic ? "0px 10px 10px 0px" : "10px 0px 0px 10px",
            },
            p: { xs: "14px 0", sm: "14px 24px" },
            fontStyle: "italic",
            fontWeight: 600,
            cursor: "pointer",
            fontSize: { xs: "18px", sm: "20px" },
            textAlign: "center",
            flex: { xs: 1, sm: "none" },
            borderRight: {
              xs: !isArabic ? "1px solid rgba(255,255,255,0.6)" : "none",
              sm: "none",
            },
            borderLeft: {
              xs: isArabic ? "1px solid rgba(255,255,255,0.6)" : "none",
              sm: "none",
            },
            textTransform: "uppercase",
            order: { xs: 1, sm: 2 },
          }}
        >
          {applyText}{" "}
        </Typography>

        {/* Calculator */}
        {!noCalc && (
          <Box
            onClick={handleCalcClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            sx={{
              bgcolor: { xs: "transparent", sm: colors.orange },
              borderRadius: {
                xs: 0,
                sm: isArabic ? "0px 10px 10px 0px" : "10px 0px 0px 10px",
              },
              p: { xs: "8px 0", sm: "8px" },
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: { xs: 1, sm: "none" },
              maxWidth: { xs: "none", sm: "160px" },
              order: { xs: 2, sm: 1 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "fit-content",
                mx: "auto",
                px: { xs: 2, sm: 0 },
              }}
            >
              <Image
                src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/calc-icon.svg"
                width={30}
                height={30}
                alt="calc icon"
              />
              {(isMobile || hoverCalc) && (
                <CustomText text={t.calcTitle} type="xxs" />
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
});

export default function ProductMain({
  heroData,
  onApplyClick,
  onCalcClick,
  onExploreMoreClick,
  imgStyle,
  staticTexts,
  headerH1 = false,
  noCalc,
  enableHtml = false,
  shadow = false,
  shadowBottom = -50,
}) {
  const { locale } = useI18n();
  const isArabic = locale === "ar";
  const hero = heroData?.[0];
  const content = useMemo(() => {
    if (!hero) return null;

    return {
      titleLine1: hero?.heading_badge?.text?.[locale],
      titleLine1Type: hero?.heading_badge?.type || "h1",
      titleLine2: hero?.heading_title?.text?.[locale],
      titleLine2Type: hero?.heading_title?.type || "h1",
      mainImage: hero?.img,
      imgAlt: hero?.alt_img,
      /*************** */
      highlightText: hero?.highlight_text?.text?.[locale],
      highlightType: hero?.highlight_text?.type || "h2",
      subTitle: hero?.sub_description.text[locale],
      subTitleType: hero?.sub_description.text.type || "p",
      description: hero?.description?.text?.[locale],
      descriptionType: hero?.description?.type || "p",
      subtitle: hero?.apply_sub_title?.text?.[locale],
      subtitleType: hero?.apply_sub_title?.type || "p",
      buttonText: staticTexts?.apply_now?.value?.text[locale],
      buttonType: staticTexts?.apply_now?.value?.type || "p",
    };
  }, [hero, locale, staticTexts]);
  // Single media query using theme breakpoints (more consistent with MUI)
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Memoize translations + derived text splits
  const t = useMemo(() => (locale === "en" ? en : ar), [locale]);

  const { firstWord, restWords, isTwoWords } = useMemo(() => {
    const line1 = content?.titleLine1 || "";
    const parts = line1.split(" ").filter(Boolean);
    const fw = parts.shift() || "";
    const rw = parts.join(" ");

    const title2 = (content?.titleLine2 || "").trim();
    const count = title2 ? title2.split(/\s+/).length : 0;

    return { firstWord: fw, restWords: rw, isTwoWords: count === 2 };
  }, [content?.titleLine1, content?.titleLine2]);

  const handleScrollToForm = useCallback(() => {
    const formSection = document.getElementById("apply-form");
    if (formSection) formSection.scrollIntoView({ behavior: "smooth" });
  }, []);

  const handleExplore = useCallback(() => {
    onExploreMoreClick?.();
  }, [onExploreMoreClick]);

  return (
    <Box sx={{ bgcolor: colors.navy }}>
      <SectionTopLine img="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/contact-line.svg" />

      <Box
        className="horizontal-safe-padding vertical-safe-padding"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: { xs: "column", lg: "row" },
          gap: { xs: 1, lg: 0 },
          color: "#fff",
          bgcolor: colors.navy,
          overflowX: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          <Box
            data-aos={"fade-down"}
            data-aos-delay="500"
            sx={{ position: "relative", display: "inline-block" }}
          >
            <Typography
              variant={content?.titleLine1Type}
              sx={{
                position: "relative",
                fontSize: { xs: "50px", md: "70px", lg: "90px" },
                fontWeight: "bold",
                lineHeight: !isArabic && 0.9,
                textTransform: "capitalize",
              }}
            >
              {firstWord} <br /> {restWords}
              <Box
                component="span"
                sx={{
                  position: "absolute",
                  bottom: { xs: 0, md: 0 },
                  [!isArabic && "right"]: { xs: 0, sm: "50%", lg: 0 },
                  [isArabic && "left"]: { xs: 0, sm: "50%", lg: 0 },
                  transform: {
                    xs: isArabic
                      ? isTwoWords
                        ? "translateY(90%) translateX(5px)"
                        : "translateY(90%) translateX(30px)"
                      : "translateY(90%) translateX(-50px)",
                    sm: isArabic
                      ? "translateY(90%) translateX(-50px)"
                      : "translateY(90%) translateX(-50px)",
                    md: isArabic
                      ? "translateY(90%) translateX(-100px)"
                      : "translateY(90%) translateX(200px)",
                    lg: isArabic
                      ? "translateY(90%) translateX(200px)"
                      : "translateY(90%) translateX(-200px)",
                    xl: isArabic
                      ? isTwoWords
                        ? "translateY(90%) translateX(200px)"
                        : "translateY(90%) translateX(300px)"
                      : "translateY(90%) translateX(-200px)",
                  },
                  fontSize: { xs: "35px", md: "60px" },
                  fontWeight: 300,
                  whiteSpace: "nowrap",
                  display: "inline-block",
                  pointerEvents: "none",
                }}
              >
                {content?.titleLine2}
              </Box>
            </Typography>
          </Box>

          <Box
            data-aos={isArabic ? "fade-left" : "fade-right"}
            data-aos-delay="500"
            sx={{
              position: "relative",
              display: "inline-block",

              "&::after": {
                content: '""',
                position: "absolute",
                bottom: shadowBottom,
                left:
                  locale === "ar"
                    ? { xs: "60%", md: "80%", lg: "60%" }
                    : { xs: "50%", md: "15%", lg: "50%" },
                right: "auto",
                transform: "translateX(-50%)",
                width: "1024px",
                height: "130px",
                background:
                  "radial-gradient(50% 50% at 50% 50%, #000 0%, rgba(0, 0, 0, 0.00) 100%)",
                filter: "blur(6px)",
                zIndex: -1,
                borderRadius: "1024",
                opacity: !shadow ? "0" : "0.6",
              },
            }}
          >
            <OutImgSection
              img={content?.mainImage}
              altImg={content?.imgAlt}
              side={isArabic ? "right" : "left"}
              customStyle={{
                zIndex: 1,
                transform: {
                  xs: isArabic
                    ? "translateX(5%) scaleX(-1)"
                    : "translateX(-5%) scaleX(1)",
                  sm: isArabic
                    ? "translateX(80px) scaleX(-1)"
                    : "translateX(-80px) scaleX(1)",
                  md: isArabic
                    ? "translateX(45%) scaleX(-1)"
                    : "translateX(-45%) scaleX(1)",
                  lg: isArabic
                    ? "translateX(12%) scaleX(-1)"
                    : "translateX(-12%) scaleX(1)",
                  xl: isArabic
                    ? "translateX(0%) scaleX(-1)"
                    : "translateX(0%) scaleX(1)",
                },
                borderRadius: "none",
                ...imgStyle,
              }}
            />
          </Box>
        </Box>

        <Box>
          {content?.highlightText && (
            <CustomText
              text={content?.highlightText}
              type="xxl"
              customStyle={{
                color: colors.dark_yellow,
                fontWeight: "800",
                mb: 2,
                lineHeight: 1,
              }}
              animation={true}
              variant={content?.highlightType}
            />
          )}

          {isMobile && (
            <ButtonsBox
              locale={locale}
              isArabic={isArabic}
              t={t}
              isMobile={isMobile}
              noCalc={noCalc}
              onApplyClick={onApplyClick}
              onCalcClick={onCalcClick}
              applyText={
                content?.buttonText ||
                (locale === "ar" ? "قدم الآن" : "Apply Now")
              }
            />
          )}

          {content?.subTitle && (
            <CustomText
              text={content?.subTitle}
              type="l"
              customStyle={{ mb: 2 }}
              animation={true}
              variant={content?.subTitleType}
            />
          )}
          {content?.description && (
            <CustomText
              text={content?.description}
              type="xs"
              customStyle={{
                my: 2,
                lineHeight: "24px",
                fontWeight: "300",
                mb: 4,
              }}
              animation={true}
              variant={content?.descriptionType}
              enableHtml={enableHtml}
            />
          )}

          <SmallTitle
            title={content?.buttonText}
            h1="p"
            customeStyle={{
              color: colors.white,
              fontStyle: "italic",
              fontSize: "24px",
              cursor: "pointer",
              mb: 0,
              textTransform: "uppercase",
            }}
            handleClick={handleScrollToForm}
            animation={true}
          />

          <CustomText
            text={content?.subtitle}
            type="xs"
            customStyle={{ mt: 2, fontWeight: "700" }}
            animation={true}
            variant={content?.subtitleType}
          />

          {!isMobile && (
            <ButtonsBox
              locale={locale}
              isArabic={isArabic}
              t={t}
              isMobile={isMobile}
              noCalc={noCalc}
              onApplyClick={onApplyClick}
              onCalcClick={onCalcClick}
              applyText={
                content?.buttonText ||
                (locale === "ar" ? "قدم الآن" : "Apply Now")
              }
            />
          )}

          <Box sx={{ display: "flex", justifyContent: "end", mt: 2 }}>
            <Box
              role="link"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                color: "#FFFFFF",
                opacity: 0.85,
                borderRadius: 1,
                "&:hover img": { animationDuration: "1.2s" },
              }}
              onClick={handleExplore}
            >
              <Box
                component="img"
                src="/explore-icon.svg"
                alt="explore-icon"
                sx={{
                  display: "block",
                  animation: `${floatFade} 1.8s ease-in-out infinite`,
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                  },
                }}
              />
              <Typography
                sx={{ fontSize: { sm: "15px" }, textTransform: "capitalize" }}
              >
                {staticTexts?.explore_more?.value?.text?.[locale] ||
                  (locale === "ar" ? "استكشف المزيد" : "Explore More")}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
